import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar, Clock, User, Phone, Mail, MessageSquare, CheckCircle2, ArrowRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { ALL_SERVICE_NAMES, TIME_SLOTS, BRAND } from "../lib/data";
import { createBooking } from "../lib/api";
import { toast } from "sonner";

const today = () => {
  const d = new Date();
  d.setDate(d.getDate());
  return d.toISOString().slice(0, 10);
};

export default function Booking() {
  useReveal();
  const [params] = useSearchParams();
  const presetService = params.get("service") || "";

  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    service: presetService, preferred_date: "", preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (presetService) setForm((f) => ({ ...f, service: presetService }));
  }, [presetService]);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createBooking(form);
      setConfirmation(res);
      toast.success("Booking received! We'll be in touch shortly.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-28 pb-24 bg-[#0B0B0B] text-white">
      <section className="px-6 md:px-12 py-12 text-center">
        <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">Reserve Your Visit</span>
        <h1 className="font-display text-5xl md:text-6xl mt-5">Book An Appointment</h1>
        <p className="text-white/65 max-w-2xl mx-auto mt-5 text-sm md:text-base">Pick your service and preferred time — we'll confirm via call or message.</p>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {confirmation ? (
          <div data-testid="booking-confirmation" className="reveal bg-gradient-to-br from-[#1A1A1A] to-[#0B0B0B] border border-[#C9A34E]/40 rounded-3xl p-10 md:p-14 text-center">
            <CheckCircle2 className="mx-auto text-[#C9A34E]" size={56} />
            <h2 className="font-display text-3xl md:text-4xl mt-6">Thank you, {confirmation.name.split(" ")[0]}!</h2>
            <p className="text-white/70 mt-4 max-w-md mx-auto">Your booking request for <span className="text-[#C9A34E]">{confirmation.service}</span> on <span className="text-[#C9A34E]">{confirmation.preferred_date}</span> at <span className="text-[#C9A34E]">{confirmation.preferred_time}</span> has been received. We'll be in touch soon.</p>
            <div className="flex flex-wrap justify-center gap-4 mt-9">
              <a href={`tel:${BRAND.phone}`} className="border border-[#C9A34E] text-[#C9A34E] rounded-full px-7 py-3 text-sm tracking-[0.22em] uppercase hover:bg-[#C9A34E] hover:text-[#0B0B0B] transition-colors">Call Salon</a>
              <Link to="/" className="bg-[#C9A34E] text-[#0B0B0B] rounded-full px-7 py-3 text-sm tracking-[0.22em] uppercase hover:bg-[#b08d3e] transition-colors">Back Home</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 bg-[#141414] border border-[#C9A34E]/20 rounded-3xl p-7 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field icon={User} label="Full Name *">
                  <input data-testid="booking-name" required value={form.name} onChange={handle("name")} className="enrich-input" placeholder="Your name" />
                </Field>
                <Field icon={Phone} label="Phone *">
                  <input data-testid="booking-phone" required value={form.phone} onChange={handle("phone")} className="enrich-input" placeholder="04xx xxx xxx" />
                </Field>
                <Field icon={Mail} label="Email *" full>
                  <input data-testid="booking-email" required type="email" value={form.email} onChange={handle("email")} className="enrich-input" placeholder="you@email.com" />
                </Field>
                <Field label="Service *" full>
                  <select data-testid="booking-service" required value={form.service} onChange={handle("service")} className="enrich-input">
                    <option value="">Select a service…</option>
                    {ALL_SERVICE_NAMES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </Field>
                <Field icon={Calendar} label="Preferred Date *">
                  <input data-testid="booking-date" required type="date" min={today()} value={form.preferred_date} onChange={handle("preferred_date")} className="enrich-input" />
                </Field>
                <Field icon={Clock} label="Preferred Time *">
                  <select data-testid="booking-time" required value={form.preferred_time} onChange={handle("preferred_time")} className="enrich-input">
                    <option value="">Select a time…</option>
                    {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field icon={MessageSquare} label="Message" full>
                  <textarea data-testid="booking-message" rows={4} value={form.message} onChange={handle("message")} className="enrich-input resize-none" placeholder="Anything we should know? (allergies, preferences…)" />
                </Field>
              </div>
              <button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#b08d3e] disabled:opacity-60 rounded-full px-10 py-4 text-sm tracking-[0.22em] uppercase font-medium transition-colors"
              >
                {submitting ? "Sending…" : "Confirm Booking"} <ArrowRight size={16} />
              </button>
            </div>

            <aside className="lg:col-span-5 space-y-6">
              <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0B0B0B] border border-[#C9A34E]/30 rounded-3xl p-7">
                <h3 className="font-display text-2xl">Visit Us</h3>
                <p className="text-sm text-white/70 mt-4">{BRAND.address}</p>
                <p className="text-sm text-white/70 mt-2">
                  <a href={`tel:${BRAND.phone}`} className="text-[#C9A34E] hover:underline">{BRAND.phone}</a>
                </p>
                <div className="mt-5 pt-5 border-t border-[#C9A34E]/15">
                  <p className="text-[11px] tracking-[0.28em] uppercase text-[#C9A34E]">Hours</p>
                  <ul className="text-sm text-white/70 mt-3 space-y-1.5">
                    <li className="flex justify-between"><span>Mon – Fri</span><span>9:00 – 18:30</span></li>
                    <li className="flex justify-between"><span>Saturday</span><span>9:00 – 19:00</span></li>
                    <li className="flex justify-between"><span>Sunday</span><span>10:00 – 17:00</span></li>
                  </ul>
                </div>
              </div>
              <div className="bg-[#141414] border border-[#C9A34E]/15 rounded-3xl p-7">
                <p className="text-[11px] tracking-[0.28em] uppercase text-[#C9A34E]">Need help?</p>
                <p className="text-sm text-white/70 mt-3">Message us on WhatsApp and we'll reply quickly.</p>
                <a href={`https://wa.me/${BRAND.phoneIntl}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 border border-[#C9A34E] text-[#C9A34E] rounded-full px-6 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#C9A34E] hover:text-[#0B0B0B] transition-colors">Chat on WhatsApp</a>
              </div>
            </aside>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#C9A34E] mb-2">
        {Icon && <Icon size={14} />} {label}
      </label>
      {children}
    </div>
  );
}
